import React, { useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import Warning from '@mui/icons-material/Warning'
import Typography from '@/v2/components/common/Typography'
import SelectInputField from '@/v2/components/common/SelectInputField'
import Row from '@/v2/components/common/Row'
import { range, geThisDayOnNextMonth } from '@/v2/utils/helpers'
import { toUSNumberFormat } from '@/v2/utils/format'
import {
  currencyToNumber,
  calculateAmountWithDiscount,
  calculateTotalDue,
  calculateMonthlyPayments,
  validateInstallmentPayment,
} from '@/v2/utils/paymentCalculations'

const MIN_INSTALLMENTS = 2
const MAX_INSTALLMENTS = 12
const INSTALLMENT_STEP = 1

const InstallmentFields = ({
  register,
  errors,
  theme,
  control,
  contract,
  disabled,
  state,
  setValue,
}) => {
  const watchedValues = useWatch({
    control,
    name: [
      'classification',
      'amount',
      'discountRate',
      'interestRate',
      'installments',
    ],
  })

  const defaults = control._defaultValues || {}

  const [
    classification = defaults.classification || '',
    amount = defaults.amount || '',
    discountRate = defaults.discountRate || 0,
    interestRate = defaults.interestRate || 0,
    installments = defaults.installments || 1,
  ] = watchedValues

  const calculations = useMemo(() => {
    if (!amount) {
      return {
        amountWithDiscount: 0,
        totalDue: 0,
        monthlyPayment: '0',
        initialPayment: 0,
        validation: { isValid: true, error: null },
      }
    }

    const effectiveDownPayment = contract?.downPayment || 0

    const amountWithDiscount = calculateAmountWithDiscount(amount, discountRate)
    const totalDue = calculateTotalDue(amountWithDiscount, interestRate)

    const monthlyPayment = calculateMonthlyPayments(
      totalDue,
      effectiveDownPayment,
      installments || MIN_INSTALLMENTS
    )

    const initialPayment = currencyToNumber(effectiveDownPayment)
    const validation = validateInstallmentPayment(
      totalDue,
      effectiveDownPayment
    )

    return {
      amountWithDiscount,
      totalDue,
      monthlyPayment,
      initialPayment,
      validation,
    }
  }, [amount, discountRate, interestRate, installments, contract])

  useEffect(() => {
    if (!amount || !installments) return

    const setValueOptions = {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    }

    setValue(
      'amountWithDiscount',
      calculations.amountWithDiscount,
      setValueOptions
    )
    setValue('totalDue', calculations.totalDue, setValueOptions)
    setValue('installmentAmount', calculations.monthlyPayment, setValueOptions)
  }, [amount, installments, discountRate, interestRate, calculations, setValue])

  useEffect(() => {
    if (classification === 'installments' && installments < MIN_INSTALLMENTS) {
      setValue('installments', MIN_INSTALLMENTS)
    }
  }, [classification, installments, setValue])

  if (classification !== 'installments') {
    return (
      <Box
        bgcolor={'gray.light'}
        display={
          calculations.amountWithDiscount > 0 && discountRate > 0
            ? 'block'
            : 'none'
        }
        borderRadius={2}
        px={2}
        py={2}
      >
        <Box display={'flex'} flexDirection={'column'} gap={0.5}>
          {calculations.amountWithDiscount > 0 && discountRate > 0 ? (
            <Box display={'flex'} gap={1}>
              <Typography variant={'body2'}>
                Total with {discountRate}% discount:
              </Typography>
              <Typography variant={'body2'} bold>
                {toUSNumberFormat(calculations.amountWithDiscount, true, true)}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    )
  }

  const { initialPayment, monthlyPayment, validation } = calculations
  const shouldShowSummary = installments > 1 && amount

  const remainingMonthlyPayments =
    initialPayment > 0 ? installments - 1 : installments

  return (
    <>
      <Row>
        <SelectInputField
          noMargin
          label="Installments"
          name="installments"
          id="payment-plan-installments"
          options={range(
            MIN_INSTALLMENTS,
            MAX_INSTALLMENTS,
            INSTALLMENT_STEP
          ).map(num => ({
            label: num,
            value: num,
          }))}
          color="secondary"
          error={errors?.installments?.message}
          {...register('installments', { valueAsNumber: true })}
          disabled={disabled}
        />

        <SelectInputField
          noMargin
          label="Interest rate"
          name="interest-rate"
          id="payment-plan-interest"
          options={range(0, 5, 0.5).map(interest => ({
            label: `${interest}%`,
            value: interest,
          }))}
          color="secondary"
          error={errors?.interestRate?.message}
          {...register('interestRate', {
            valueAsNumber: true,
          })}
          disabled={disabled}
        />
      </Row>

      {shouldShowSummary && (
        <Box
          bgcolor={validation.isValid ? 'gray.light' : 'error.main'}
          borderRadius={2}
          px={2}
          py={2}
          mb={2}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            {validation.isValid ? (
              <>
                {initialPayment > 0 && (
                  <Box display="flex" gap={1}>
                    <Typography variant="body2">
                      Total due today (Initial Payment):
                    </Typography>
                    <Typography variant="body2" bold>
                      {toUSNumberFormat(initialPayment, true, true)}
                    </Typography>
                  </Box>
                )}

                {calculations.amountWithDiscount > 0 && discountRate > 0 && (
                  <Box display="flex" gap={1}>
                    <Typography variant="body2">
                      Total with {discountRate}% discount:
                    </Typography>
                    <Typography variant="body2" bold>
                      {toUSNumberFormat(
                        calculations.amountWithDiscount,
                        true,
                        true
                      )}
                    </Typography>
                  </Box>
                )}

                {calculations.totalDue && interestRate > 0 && (
                  <Box display="flex" gap={1}>
                    <Typography variant="body2">
                      Total with {interestRate}% interest:
                    </Typography>
                    <Typography variant="body2" bold>
                      {toUSNumberFormat(calculations.totalDue, true, true)}
                    </Typography>
                  </Box>
                )}

                {monthlyPayment > 0 && remainingMonthlyPayments > 0 && (
                  <>
                    <Box display="flex" gap={1}>
                      <Typography variant="body2">
                        {remainingMonthlyPayments}{' '}
                        {remainingMonthlyPayments === 1
                          ? 'payment'
                          : 'payments'}{' '}
                        of {toUSNumberFormat(monthlyPayment, true, true)}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={1}>
                      <Typography
                        fontWeight="light"
                        variant="body3"
                        color="fieldLabel.main"
                      >
                        Next payment {geThisDayOnNextMonth()}
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Typography
                variant="body2"
                color="white.main"
                fontWeight="light"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Warning />
                {validation.error}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

export default InstallmentFields
